import { fakerDE as faker } from "@faker-js/faker";
import { z } from "zod";
import { uniqueArray } from "../../array";
import type { Enums } from "../../types";

export const vehicleManufacturers = uniqueArray(
	faker.definitions.vehicle.manufacturer.concat(["MAN", "Scania", "Iveco", "Freightliner"]),
).sort();

export const vehicleTypes: Array<Enums<"vehicleType">> = [
	"truck",
	"car",
	"pickup",
	"trailer",
	"van",
	"other",
] as const;

export const fuelTypes = ["diesel", "petrol", "electric", "hybrid", "other"] as const;
export const cranePosition = ["front", "tail"] as const;

export const craneFormSchema = z.object({
	exists: z.coerce.boolean().default(false),
	name: z.string().min(2).max(100).default("unnamed"),
	position: z.enum(cranePosition).optional(),
	angle: z.number().min(0).default(0),
	maximumLoad: z
		.array(
			z.object({
				meter: z.number().min(0).default(0),
				weightKg: z.number().min(0).default(0),
				adcKg: z.number().min(0).default(0),
			}),
		)
		.nullish()
		.default([]),
});

export const vehicleFormSchema = z.object({
	licensePlate: z
		.preprocess((val) => (typeof val === "string" ? val.split(" ") : val), z.array(z.string()))
		.transform((values) => values.map((segment) => segment.trim().toUpperCase())),
	type: z.enum(vehicleTypes),
	vin: z
		.string()
		.transform((value) => {
			if (!value) return null;
			return value.trim().toUpperCase();
		})
		.nullish(),
	manufacturer: z.string().nullish(),
	fuelType: z.enum(fuelTypes),

	hsn: z.string().min(1).nullish(),
	tsn: z.string().min(1).nullish(),

	maximumLoadKg: z.number().min(0).nullish(),
	maximumLoadVolume: z.number().min(0).nullish(),

	firstRegistration: z.string().min(1).nullish(), // YYYY-MM-DD

	depth: z.number().min(0).nullish(),
	height: z.number().min(0).nullish(),
	width: z.number().min(0).nullish(),
	flatbedDepth: z.number().min(0).nullish(),
	flatbedWidth: z.number().min(0).nullish(),

	craneFront: craneFormSchema.nullish(),
	craneTail: craneFormSchema.nullish(),
});

export type VehicleFormSchema = z.infer<typeof vehicleFormSchema>;
export type VehicleCraneFormSchema = z.infer<typeof craneFormSchema>;
